import {
  button,
  div,
  img,
  input,
  label,
  span
} from '@srfnstack/fntags'
import Sortable from 'sortablejs'
import { CreditButton } from '../../components/CreditButton.js'
import { NumberSpinner } from '../../components/NumberSpinner.js'

// The marble "hero" must be image #1 — Etsy uses the first image as the listing
// thumbnail. isHeroDemoted reports whether a hero exists but is not the leading
// (non-rejected, non-badge/plain) mockup, i.e. a manual reorder pushed it down.
const isSpecial = (m) => m.params?.type === 'quality_guarantee' || m.params?.type === 'plain_square'
const isHeroDemoted = (list) => {
  if (!list.some(m => m.params?.type === 'hero')) return false
  const lead = list.find(m => !m.rejected && !isSpecial(m))
  return !!lead && lead.params?.type !== 'hero'
}

export const MockupPanel = ({ mockupsList, mockupCount, selectedAsset, viewerAsset, product, showRejectedMockups, handleUpdate, handleGenerateMoreMockups, toggleMockupReject }) =>
  div({ class: 'flex-1 w-full space-y-4 lg:overflow-y-auto lg:min-h-0' },
    mockupsList.bindAs(list => isHeroDemoted(list)
      ? div({ class: 'alert alert-warning py-2 px-4 rounded-xl text-xs font-semibold flex items-center gap-2' },
        span({ class: 'material-symbols-rounded !text-base' }, 'warning'),
        span({ class: 'contents' }, 'The marble hero is no longer image #1. Etsy uses the first image as the thumbnail — moving the hero down will likely hurt click-through.')
      )
      : div({ style: 'display:none' })
    ),
    div({ class: 'bg-base-300 rounded-2xl aspect-square max-h-[65vh] mx-auto overflow-hidden border border-base-300 shadow-inner relative group' },
      selectedAsset.bindAs(asset => {
        if (!asset) {
          return div({ class: 'flex flex-col items-center justify-center h-full gap-4' },
            span({ class: 'loading loading-spinner loading-lg text-primary' }),
            span({ class: 'text-xs font-bold uppercase tracking-widest opacity-40' }, 'Generating Mockups...')
          )
        }
        if (asset.status === 'PENDING') {
          return div({ class: 'flex flex-col items-center justify-center h-full gap-4' },
            span({ class: 'loading loading-spinner loading-lg text-primary' }),
            span({ class: 'text-xs font-bold uppercase tracking-widest opacity-40' }, 'Processing Asset...')
          )
        }
        return div({ class: 'contents' },
          asset.file_path
            ? img({
              src: `/api/image?file=${encodeURIComponent(asset.file_path)}`,
              class: 'w-full h-full object-contain transition-transform group-hover:scale-[1.02] cursor-pointer',
              decoding: 'async',
              onclick: () => viewerAsset(asset)
            })
            : '',
          asset.type === 'MOCKUP'
            ? button({
              class: 'absolute top-4 right-4 btn btn-circle btn-error btn-sm opacity-30 hover:opacity-100 transition-opacity',
              onclick: (e) => { e.stopPropagation(); toggleMockupReject(asset.id, asset.rejected) },
              title: 'Reject Mockup'
            }, '×')
            : ''
        )
      }),
      div({ class: 'absolute bottom-4 left-1/2 -translate-x-1/2 bg-base-300/80 px-4 py-2 rounded-full text-[10px] uppercase tracking-widest font-black backdrop-blur-md border border-white/10' },
        'Product Mockup'
      )
    ),
    div({ class: 'flex items-center gap-2' },
      div({ class: 'join flex-1' },
        NumberSpinner({ state: mockupCount, min: 1, max: 20, joinItem: true }),
        CreditButton({
          actionCode: 'MOCKUP_GENERATE',
          class: 'btn btn-outline btn-sm join-item flex-grow border-dashed opacity-60 hover:opacity-100',
          onclick: handleGenerateMoreMockups,
          label: span({ class: 'flex items-center gap-2' }, span({ class: 'material-symbols-rounded' }, 'add_circle'), 'Generate More Mockups'),
          multipliers: { count: mockupCount }
        })
      ),
      label({ class: 'label cursor-pointer gap-2 py-0' },
        span({ class: 'label-text text-xs font-bold uppercase opacity-50' }, 'Show Rejected'),
        input({
          type: 'checkbox',
          class: 'toggle toggle-sm toggle-error',
          checked: showRejectedMockups.bindAttr(),
          onchange: (e) => { showRejectedMockups(e.target.checked); mockupsList([...mockupsList()]) }
        })
      )
    ),
    (() => {
      const container = div({ class: 'grid grid-cols-4 lg:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-6 gap-3' })
      mockupsList.bindChildren(
        container,
        (mState) =>
          mState.bindAs(m => {
            if (m.rejected && !showRejectedMockups()) return div({ style: 'display:none', 'data-id': m.id })
            return div({
              class: selectedAsset.bindAttr(asset => `aspect-square w-full bg-base-200 rounded-xl overflow-hidden cursor-pointer border-2 ${asset?.id === m.id ? 'border-primary shadow-lg ring-4 ring-primary/20' : m.rejected ? 'border-error opacity-40' : 'border-transparent opacity-60'} relative group/item`),
              onclick: () => !m.rejected && selectedAsset({ ...m, type: 'MOCKUP' }),
              'data-id': m.id
            },
            m.status === 'PENDING'
              ? div({ class: 'flex items-center justify-center h-full bg-base-300/50' },
                span({ class: 'loading loading-spinner loading-md text-primary' })
              )
              : div({ class: 'contents' },
                m.file_path ? img({ src: `/api/image?file=${encodeURIComponent(m.file_path)}`, class: `w-full h-full object-cover pointer-events-none ${m.rejected ? 'grayscale' : ''}`, decoding: 'async', loading: 'lazy' }) : '',
                m.rejected
                  ? button({
                    class: 'absolute inset-0 flex items-center justify-center bg-error/20 opacity-0 group-hover/item:opacity-100 transition-opacity',
                    onclick: (e) => { e.stopPropagation(); toggleMockupReject(m.id, true) },
                    title: 'Unreject'
                  }, span({ class: 'material-symbols-rounded text-error bg-base-100 rounded-full p-1' }, 'undo'))
                  : div({ class: 'drag-handle absolute top-1 left-1 bg-primary text-primary-content rounded p-1 shadow-lg cursor-grab active:cursor-grabbing z-10' },
                    span({ class: 'material-symbols-rounded !text-sm' }, 'drag_indicator')
                  )
              )
            )
          })
      )
      Sortable.create(container, {
        animation: 150,
        ghostClass: 'sortable-ghost',
        chosenClass: 'sortable-chosen',
        handle: '.drag-handle',
        onStart: () => {
          container.classList.add('dragging-active')
        },
        onEnd: (evt) => {
          container.classList.remove('dragging-active')
          const list = mockupsList()
          const item = list.splice(evt.oldIndex, 1)[0]
          list.splice(evt.newIndex, 0, item)

          mockupsList([...list])

          const newOrder = list.map(m => m.id)
          handleUpdate({ attributes: { ...product().attributes, mockup_order: newOrder } }, false)
        }
      })
      return container
    })()
  )
